import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import SignatureFlow from '../components/SignatureFlow';
import { 
  Phone, PhoneOff, Camera, Mic, MicOff, Video, VideoOff, 
  Loader2, ArrowLeft, MousePointer2, Circle, Type,
  Trash2, Save, Crosshair, PenTool, FileText, 
  ChevronRight, ChevronLeft, Archive, CheckCircle, User,
  Link2, Mail, MessageSquare, Copy, FolderPlus, FileSignature, Upload, Send, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Daily from '@daily-co/daily-js';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TOOLS = [
  { id: 'pointer', icon: MousePointer2, name: 'Pointeur', color: '#FFFFFF' },
  { id: 'arrow', icon: Crosshair, name: 'Flèche', color: '#EF4444' },
  { id: 'circle', icon: Circle, name: 'Cercle', color: '#F59E0B' },
  { id: 'freehand', icon: PenTool, name: 'Dessin', color: '#10B981' },
  { id: 'text', icon: Type, name: 'Texte', color: '#3B82F6' },
];

const getToken = () => localStorage.getItem('kardif_token');

export default function VisioTechnician() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callState, setCallState] = useState('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [selectedTool, setSelectedTool] = useState('pointer');
  const [annotations, setAnnotations] = useState([]);
  const [capturedPhotos, setCapturedPhotos] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [showReportPanel, setShowReportPanel] = useState(false);
  const [clientConnected, setClientConnected] = useState(false);
  const [remoteVideoTrack, setRemoteVideoTrack] = useState(null);
  const [reportData, setReportData] = useState({
    diagnostic: '',
    observations: '',
    recommandations: '',
    pieces_necessaires: '',
    urgence: 'normale',
    intervention_requise: false,
    notes_client: '',
    zone_humide: '',
    stigmates: '',
    pieces_sinistrees: ''
  });
  const [reportSaved, setReportSaved] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [attached, setAttached] = useState(false);
  const [docs, setDocs] = useState([]);
  const [activeDoc, setActiveDoc] = useState(null);
  const [signedDocs, setSignedDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docInputRef = useRef(null);
  const [signDoc, setSignDoc] = useState(null);
  const [signingTech, setSigningTech] = useState(false);
  
  const callObjectRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const localVideoRef = useRef(null);
  const canvasRef = useRef(null);
  const videoContainerRef = useRef(null);

  // Load session
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const res = await axios.get(`${API}/visio/sessions/${sessionId}`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        setSession(res.data);
        setDocs(res.data.documents || []);
        setSignedDocs(res.data.signed_documents || []);
        setActiveDoc(res.data.active_document || null);
      } catch (err) {
        console.error(err);
        toast.error('Session non trouvée');
        navigate('/planning');
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [sessionId, navigate]);

  // Start call with custom video handling
  const startCall = useCallback(async () => {
    if (!session || callObjectRef.current) return;
    
    setCallState('joining');
    
    try {
      const startRes = await axios.post(`${API}/visio/sessions/${sessionId}/start`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      if (startRes.data?.daily_configured === false) {
        setCallState('not_configured');
        toast.error("Visio non activée : la clé API Daily.co n'est pas configurée. Contactez l'administrateur.");
        return;
      }

      const roomUrl = startRes.data?.room_url || session.room_url || `https://rdfvisio.daily.co/${session.room_name}`;
      
      // Create Daily call object (not iframe - for custom video handling)
      const callObject = Daily.createCallObject({
        videoSource: true,
        audioSource: true,
      });
      
      callObjectRef.current = callObject;
      
      // Handle participant events
      callObject.on('joined-meeting', () => {
        setCallState('joined');
        toast.success('Appel démarré - En attente du client');
        
        // Get local video
        const localTracks = callObject.participants().local;
        if (localTracks?.videoTrack) {
          const stream = new MediaStream([localTracks.videoTrack]);
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = stream;
          }
        }
      });
      
      callObject.on('participant-joined', (event) => {
        if (!event.participant.local) {
          setClientConnected(true);
          toast.success('Le client a rejoint !');
        }
      });
      
      callObject.on('participant-updated', (event) => {
        if (!event.participant.local) {
          // Try multiple ways to get the video track
          const videoTrack = event.participant.videoTrack || 
                            event.participant.tracks?.video?.track ||
                            event.participant.tracks?.video?.persistentTrack;
          if (videoTrack) {
            setRemoteVideoTrack(videoTrack);
            const stream = new MediaStream([videoTrack]);
            if (remoteVideoRef.current) {
              remoteVideoRef.current.srcObject = stream;
              remoteVideoRef.current.play().catch(() => {});
            }
          }
          // Audio distant du client (sinon on ne l'entend pas)
          const audioTrack = event.participant.audioTrack ||
                            event.participant.tracks?.audio?.track ||
                            event.participant.tracks?.audio?.persistentTrack;
          if (audioTrack && remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = new MediaStream([audioTrack]);
            remoteAudioRef.current.play().catch(() => {});
          }
        }
      });
      
      callObject.on('track-started', (event) => {
        if (event.participant.local) return;
        if (event.track.kind === 'video') {
          setRemoteVideoTrack(event.track);
          const stream = new MediaStream([event.track]);
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = stream;
            remoteVideoRef.current.play().catch(() => {});
          }
          setClientConnected(true);
        } else if (event.track.kind === 'audio') {
          // Jouer l'audio du client
          if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = new MediaStream([event.track]);
            remoteAudioRef.current.play().catch(() => {});
          }
          setClientConnected(true);
        }
      });
      
      callObject.on('participant-left', (event) => {
        if (!event.participant.local) {
          setClientConnected(false);
          setRemoteVideoTrack(null);
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = null;
          }
          if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = null;
          }
          toast.info('Le client a quitté');
        }
      });
      
      callObject.on('left-meeting', () => {
        setCallState('ended');
      });
      
      callObject.on('error', (e) => {
        console.error('Daily error:', e);
        toast.error('Erreur de connexion');
        setCallState('idle');
      });
      
      await callObject.join({ url: roomUrl, userName: 'Technicien KARDIF' });
      
    } catch (err) {
      console.error('Start error:', err);
      toast.error('Impossible de démarrer l\'appel');
      setCallState('idle');
    }
  }, [session, sessionId]);

  // End call
  const endCall = useCallback(async () => {
    if (callObjectRef.current) {
      await callObjectRef.current.leave();
      callObjectRef.current.destroy();
      callObjectRef.current = null;
    }
    
    try {
      await axios.post(`${API}/visio/sessions/${sessionId}/end`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
    } catch (e) {}
    
    setCallState('ended');
  }, [sessionId]);

  // Toggle audio/video
  const toggleMute = () => {
    if (callObjectRef.current) {
      callObjectRef.current.setLocalAudio(isMuted);
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (callObjectRef.current) {
      callObjectRef.current.setLocalVideo(isVideoOff);
      setIsVideoOff(!isVideoOff);
    }
  };

  // Draw annotations
  const drawAnnotationsToCanvas = useCallback((ctx, width, height) => {
    ctx.clearRect(0, 0, width, height);
    
    annotations.forEach(ann => {
      const x = ann.x * width;
      const y = ann.y * height;
      
      ctx.strokeStyle = ann.color || '#EF4444';
      ctx.fillStyle = ann.color || '#EF4444';
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
      ctx.shadowBlur = 4;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
      
      switch (ann.type) {
        case 'arrow':
          if (ann.startX !== undefined && ann.endX !== undefined) {
            const startX = ann.startX * width;
            const startY = ann.startY * height;
            const endX = ann.endX * width;
            const endY = ann.endY * height;
            
            ctx.beginPath();
            ctx.moveTo(startX, startY);
            ctx.lineTo(endX, endY);
            ctx.stroke();
            
            const angle = Math.atan2(endY - startY, endX - startX);
            const headLength = 25;
            ctx.beginPath();
            ctx.moveTo(endX, endY);
            ctx.lineTo(endX - headLength * Math.cos(angle - Math.PI / 6), endY - headLength * Math.sin(angle - Math.PI / 6));
            ctx.lineTo(endX - headLength * Math.cos(angle + Math.PI / 6), endY - headLength * Math.sin(angle + Math.PI / 6));
            ctx.closePath();
            ctx.fill();
          }
          break;
          
        case 'circle':
          ctx.beginPath();
          const radius = (ann.radius || 0.05) * Math.min(width, height);
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.stroke();
          break;
          
        case 'freehand':
          if (ann.path && ann.path.length > 1) {
            ctx.beginPath();
            ctx.moveTo(ann.path[0].x * width, ann.path[0].y * height);
            ann.path.forEach(p => ctx.lineTo(p.x * width, p.y * height));
            ctx.stroke();
          }
          break;
          
        case 'text':
          if (ann.text) {
            ctx.font = 'bold 22px Arial';
            ctx.fillText(ann.text, x, y);
          }
          break;
      }
      
      ctx.shadowColor = 'transparent';
    });
    
    // Current freehand path
    if (currentPath.length > 1) {
      ctx.strokeStyle = '#10B981';
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(currentPath[0].x * width, currentPath[0].y * height);
      currentPath.forEach(p => ctx.lineTo(p.x * width, p.y * height));
      ctx.stroke();
    }
  }, [annotations, currentPath]);

  // Canvas handlers
  const handleCanvasMouseDown = (e) => {
    if (selectedTool === 'pointer') return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    setIsDrawing(true);
    
    const tool = TOOLS.find(t => t.id === selectedTool);
    
    if (selectedTool === 'freehand') {
      setCurrentPath([{ x, y }]);
    } else if (selectedTool === 'arrow') {
      setAnnotations(prev => [...prev, {
        id: Date.now(), type: 'arrow', startX: x, startY: y, endX: x, endY: y, color: tool?.color, x, y
      }]);
    } else if (selectedTool === 'text') {
      const text = prompt('Entrez le texte:');
      if (text) {
        setAnnotations(prev => [...prev, { id: Date.now(), type: 'text', x, y, text, color: tool?.color }]);
      }
      setIsDrawing(false);
    } else if (selectedTool === 'circle') {
      setAnnotations(prev => [...prev, { id: Date.now(), type: 'circle', x, y, radius: 0.02, color: tool?.color }]);
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (!isDrawing) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    if (selectedTool === 'freehand') {
      setCurrentPath(prev => [...prev, { x, y }]);
    } else if (selectedTool === 'arrow') {
      setAnnotations(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last?.type === 'arrow') {
          updated[updated.length - 1] = { ...last, endX: x, endY: y };
        }
        return updated;
      });
    } else if (selectedTool === 'circle') {
      setAnnotations(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last?.type === 'circle') {
          const dx = x - last.x;
          const dy = y - last.y;
          updated[updated.length - 1] = { ...last, radius: Math.sqrt(dx*dx + dy*dy) };
        }
        return updated;
      });
    }
  };

  const handleCanvasMouseUp = () => {
    if (!isDrawing) return;
    
    if (selectedTool === 'freehand' && currentPath.length > 1) {
      setAnnotations(prev => [...prev, {
        id: Date.now(), type: 'freehand', path: currentPath, color: '#10B981', x: currentPath[0].x, y: currentPath[0].y
      }]);
      setCurrentPath([]);
    }
    setIsDrawing(false);
  };

  // Send annotations to backend
  useEffect(() => {
    if (callState !== 'joined') return;
    
    const send = async () => {
      try {
        await axios.post(`${API}/visio/sessions/${sessionId}/annotations`, { annotations }, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
      } catch (e) {}
    };
    send();
  }, [annotations, callState, sessionId]);

  const clearAnnotations = () => {
    setAnnotations([]);
    toast.info('Annotations effacées');
  };

  // Capture photo - Screenshot de toute l'interface vidéo
  const capturePhoto = async () => {
    const container = videoContainerRef.current;
    
    if (!container) {
      toast.error('Zone vidéo non disponible');
      return;
    }
    
    try {
      toast.info('Capture en cours...');
      
      // Utiliser html2canvas pour capturer tout le conteneur vidéo (avec la vidéo + annotations)
      const canvas = await html2canvas(container, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#000000',
        scale: 1,
        logging: false,
      });
      
      const imageData = canvas.toDataURL('image/jpeg', 0.9);
      setCapturedPhotos(prev => [...prev, { id: Date.now(), data: imageData, timestamp: new Date().toISOString() }]);
      toast.success('Photo capturée !');
    } catch (err) {
      console.error('Capture error:', err);
      toast.error('Erreur lors de la capture');
    }
  };

  // Download ZIP
  const downloadPhotosAsZip = async () => {
    if (capturedPhotos.length === 0) return toast.error('Aucune photo');
    
    const zip = new JSZip();
    capturedPhotos.forEach((photo, i) => {
      zip.file(`photo_${i+1}.jpg`, photo.data.split(',')[1], { base64: true });
    });
    
    if (reportData.diagnostic || reportData.observations) {
      zip.file('rapport.txt', `RAPPORT VISIO KARDIF\n\nDiagnostic: ${reportData.diagnostic}\nObservations: ${reportData.observations}\nRecommandations: ${reportData.recommandations}`);
    }
    
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `visio_${session?.client_name?.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.zip`);
    toast.success('ZIP téléchargé');
  };

  // Save report
  const saveReport = async () => {
    try {
      await axios.put(`${API}/visio/sessions/${sessionId}`, { notes: JSON.stringify(reportData) }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setReportSaved(true);
      toast.success('Rapport sauvegardé');
      setTimeout(() => setReportSaved(false), 3000);
    } catch (e) {
      toast.error('Erreur');
    }
  };

  // Client link helpers (visible at all times)
  const clientLink = session ? `${window.location.origin}/visio/${session.token}` : '';
  const shareMessage = `Bonjour, voici votre lien de Visio-Assistance KARDIF : ${clientLink}`;
  const copyClientLink = () => {
    navigator.clipboard?.writeText(clientLink).then(
      () => toast.success('Lien client copié'),
      () => toast.error('Copie impossible')
    );
  };
  const smsHref = `sms:${session?.client_phone || ''}?&body=${encodeURIComponent(shareMessage)}`;
  const emailHref = `mailto:?subject=${encodeURIComponent('Votre Visio-Assistance KARDIF')}&body=${encodeURIComponent(shareMessage)}`;

  // Attach photos + report to the linked intervention dossier
  const attachToIntervention = async () => {
    if (!session?.intervention_id) {
      toast.error("Aucun dossier d'intervention n'est lié à cette visio");
      return;
    }
    const hasReport = reportData.diagnostic || reportData.observations || reportData.recommandations;
    if (capturedPhotos.length === 0 && !hasReport) {
      toast.error('Rien à enregistrer : capturez une photo ou rédigez le rapport');
      return;
    }
    setAttaching(true);
    try {
      const res = await axios.post(`${API}/visio/sessions/${sessionId}/attach`, {
        photos: capturedPhotos.map(p => p.data),
        report: {
          diagnostic: reportData.diagnostic,
          observations: reportData.observations,
          zone_humide: reportData.zone_humide,
          stigmates: reportData.stigmates,
          pieces_sinistrees: reportData.pieces_sinistrees,
          recommandations: reportData.recommandations,
        },
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      setAttached(true);
      toast.success(`Enregistré dans le dossier (${res.data.documents_added} élément(s))`);
      setTimeout(() => setAttached(false), 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement dans le dossier");
    } finally {
      setAttaching(false);
    }
  };

  // --- Documents à faire signer ---
  const fileHref = (u) => (!u ? '' : (/^https?:\/\//.test(u) ? u : `${process.env.REACT_APP_BACKEND_URL}${u.startsWith('/') ? '' : '/'}${u}`));

  // Poll documents state (detect when client signs)
  useEffect(() => {
    if (!sessionId) return;
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/visio/signed-documents/${sessionId}`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        setActiveDoc(res.data.active_document || null);
        setSignedDocs((prev) => {
          const next = res.data.signed_documents || [];
          if (next.length > prev.length) toast.success('Le client a signé un document !');
          return next;
        });
        if (res.data.documents) setDocs(res.data.documents);
      } catch (e) {}
    }, 2500);
    return () => clearInterval(poll);
  }, [sessionId]);

  const uploadSignDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Veuillez choisir un fichier PDF'); return; }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${getToken()}` } });
      const added = await axios.post(`${API}/visio/sessions/${sessionId}/documents`,
        { name: file.name, url: up.data.url },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      setDocs((prev) => [...prev, added.data]);
      toast.success('Document ajouté');
    } catch (err) {
      toast.error("Échec de l'ajout du document");
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const pushDoc = async (doc) => {
    try {
      await axios.post(`${API}/visio/sessions/${sessionId}/push-document`,
        { id: doc.id, name: doc.name, url: doc.url },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      setActiveDoc({ id: doc.id, name: doc.name, url: doc.url });
      toast.success('Document envoyé au client pour signature');
    } catch (e) {
      toast.error("Échec de l'envoi au client");
    }
  };

  const unpushDoc = async () => {
    try {
      await axios.post(`${API}/visio/sessions/${sessionId}/unpush-document`, {},
        { headers: { Authorization: `Bearer ${getToken()}` } });
      setActiveDoc(null);
      toast.info('Document retiré');
    } catch (e) {}
  };

  const shareSigned = (sd) => {
    const link = fileHref(sd.url);
    navigator.clipboard?.writeText(link).then(
      () => toast.success('Lien du document signé copié'),
      () => toast.error('Copie impossible')
    );
  };

  const techSignComplete = async (signature, placements) => {
    if (!signDoc || !signature) return;
    setSigningTech(true);
    try {
      const res = await axios.post(`${API}/visio/sessions/${sessionId}/sign-document`,
        { url: signDoc.url, name: signDoc.name, signature, signatures: placements?.signatures || [], texts: placements?.texts || [] },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      setSignedDocs((prev) => [...prev, res.data.document]);
      setSignDoc(null);
      toast.success('Document signé' + (res.data.attached ? ' et attaché au dossier' : ''));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la signature');
    } finally {
      setSigningTech(false);
    }
  };

  // Redraw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const video = remoteVideoRef.current;
    if (!canvas || callState !== 'joined') return;
    
    const resize = () => {
      if (video && videoContainerRef.current) {
        const rect = videoContainerRef.current.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;
      }
      const ctx = canvas.getContext('2d');
      drawAnnotationsToCanvas(ctx, canvas.width, canvas.height);
    };
    
    resize();
    const interval = setInterval(resize, 100);
    window.addEventListener('resize', resize);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resize);
    };
  }, [annotations, currentPath, callState, drawAnnotationsToCanvas]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (callObjectRef.current) {
        callObjectRef.current.destroy();
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-[#8B7355] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col">
      {/* Header */}
      <div className="bg-[#2A2A2A] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/planning')} className="text-white hover:bg-[#3A3A3A]">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <span className="text-white font-medium">Visio-Assistance</span>
          <span className="text-gray-400">{session?.client_name}</span>
        </div>
        <div className="flex items-center gap-2">
          {clientConnected && (
            <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <User className="h-3 w-3" /> Client connecté
            </span>
          )}
          {callState === 'joined' && (
            <span className="bg-red-600 text-white text-xs px-3 py-1 rounded-full animate-pulse">En direct</span>
          )}
        </div>
      </div>

      {/* Barre lien client — visible en permanence */}
      <div className="bg-[#232323] border-b border-[#3A3A3A] px-4 py-2 flex flex-wrap items-center gap-2" data-testid="visio-client-link-bar">
        <span className="text-gray-400 text-xs flex items-center gap-1"><Link2 className="h-3.5 w-3.5" /> Lien client :</span>
        <code className="text-[#C9A26B] text-xs bg-[#1A1A1A] px-2 py-1 rounded truncate max-w-[46vw]" data-testid="visio-client-link-value">{clientLink}</code>
        <Button size="sm" onClick={copyClientLink} className="h-7 bg-[#4A4A4A] hover:bg-[#5A5A5A]" data-testid="visio-copy-link">
          <Copy className="h-3.5 w-3.5 mr-1" /> Copier
        </Button>
        <a href={smsHref} data-testid="visio-sms-link">
          <Button size="sm" className="h-7 bg-[#4A4A4A] hover:bg-[#5A5A5A]"><MessageSquare className="h-3.5 w-3.5 mr-1" /> SMS</Button>
        </a>
        <a href={emailHref} data-testid="visio-email-link">
          <Button size="sm" className="h-7 bg-[#4A4A4A] hover:bg-[#5A5A5A]"><Mail className="h-3.5 w-3.5 mr-1" /> Email</Button>
        </a>
      </div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Zone vidéo principale */}
        <div className="flex-1 relative bg-black" ref={videoContainerRef}>
          {/* Audio du CLIENT (nécessaire pour l'entendre en mode call object) */}
          <audio ref={remoteAudioRef} autoPlay playsInline />
          {/* Vidéo du CLIENT en plein écran */}
          {callState === 'joined' && (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="absolute inset-0 w-full h-full object-contain bg-black"
            />
          )}
          
          {/* Canvas pour annotations - exactement sur la vidéo */}
          {callState === 'joined' && (
            <canvas
              ref={canvasRef}
              className="absolute inset-0 w-full h-full"
              style={{ 
                cursor: selectedTool !== 'pointer' ? 'crosshair' : 'default',
                pointerEvents: selectedTool !== 'pointer' ? 'auto' : 'none'
              }}
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={() => setIsDrawing(false)}
            />
          )}
          
          {/* Ma vidéo en miniature */}
          {callState === 'joined' && (
            <div className="absolute bottom-4 left-4 w-40 h-28 rounded-lg overflow-hidden border-2 border-white/30 shadow-xl z-20">
              <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              <span className="absolute bottom-1 left-2 text-white text-xs bg-black/50 px-1 rounded">Vous</span>
            </div>
          )}
          
          {/* Indicateurs */}
          {callState === 'joined' && !clientConnected && (
            <div className="absolute top-4 left-4 z-20 bg-amber-600 rounded-lg px-4 py-2">
              <p className="text-white text-sm">En attente du client...</p>
            </div>
          )}
          
          {/* Indicateur : client connecté mais pas de vidéo */}
          {callState === 'joined' && clientConnected && !remoteVideoTrack && (
            <div className="absolute top-4 left-4 z-20 bg-blue-600 rounded-lg px-4 py-2">
              <p className="text-white text-sm">Client connecté - En attente de sa caméra...</p>
            </div>
          )}
          
          {callState === 'joined' && selectedTool !== 'pointer' && (
            <div className="absolute top-4 left-4 z-20 bg-red-600 rounded-lg px-4 py-2">
              <p className="text-white text-sm font-medium">Mode annotation - Dessinez sur la vidéo du client</p>
            </div>
          )}
          
          {/* Écran d'attente */}
          {callState === 'idle' && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#1A1A1A]">
              <Card className="max-w-md w-full mx-4 bg-[#2A2A2A] border-[#3A3A3A]">
                <CardContent className="p-8 text-center">
                  <Video className="h-16 w-16 text-[#8B7355] mx-auto mb-4" />
                  <h2 className="text-xl font-bold text-white mb-2">Prêt à démarrer ?</h2>
                  <p className="text-gray-400 mb-4">Client: {session?.client_name}</p>
                  <div className="bg-[#3A3A3A] rounded-lg p-4 mb-6 text-left">
                    <p className="text-white text-sm mb-1">Lien client :</p>
                    <code className="text-[#8B7355] text-xs">{window.location.origin}/visio/{session?.token}</code>
                  </div>
                  <Button data-testid="visio-start-call" onClick={startCall} className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg">
                    <Phone className="h-5 w-5 mr-2" /> Démarrer l'appel
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
          
          {callState === 'joining' && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#1A1A1A]">
              <Loader2 className="h-12 w-12 text-white animate-spin" />
            </div>
          )}

          {callState === 'not_configured' && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#1A1A1A]" data-testid="visio-not-configured">
              <Card className="max-w-md w-full mx-4 bg-[#2A2A2A] border-[#3A3A3A]">
                <CardContent className="p-8 text-center">
                  <Video className="h-16 w-16 text-amber-500 mx-auto mb-4" />
                  <h2 className="text-xl font-bold text-white mb-2">Visio non encore activée</h2>
                  <p className="text-gray-400 mb-6 text-sm">
                    La clé API Daily.co n'est pas configurée sur le serveur. L'administrateur doit l'ajouter
                    (variable <code className="text-[#8B7355]">DAILY_API_KEY</code>) pour permettre les appels vidéo.
                  </p>
                  <Button onClick={() => setCallState('idle')} className="w-full bg-[#8B7355] hover:bg-[#7A6548]">
                    Compris
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
          
          {callState === 'ended' && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#1A1A1A]">
              <Card className="max-w-md bg-[#2A2A2A] border-[#3A3A3A]">
                <CardContent className="p-8 text-center">
                  <h2 className="text-xl font-bold text-white mb-4">Appel terminé</h2>
                  {session?.intervention_id && (capturedPhotos.length > 0 || reportData.diagnostic || reportData.observations || reportData.recommandations) && (
                    <Button onClick={attachToIntervention} disabled={attaching} className="w-full bg-[#8B7355] hover:bg-[#7A6548] mb-3" data-testid="visio-attach-dossier-ended">
                      {attaching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : attached ? <CheckCircle className="h-4 w-4 mr-2" /> : <FolderPlus className="h-4 w-4 mr-2" />}
                      {attached ? 'Enregistré dans le dossier' : 'Enregistrer dans le dossier'}
                    </Button>
                  )}
                  {capturedPhotos.length > 0 && (
                    <Button onClick={downloadPhotosAsZip} className="w-full bg-blue-600 mb-3">
                      <Archive className="h-4 w-4 mr-2" /> Télécharger {capturedPhotos.length} photo(s)
                    </Button>
                  )}
                  <Button onClick={() => navigate('/planning')} className="w-full bg-[#8B7355]">Retour au planning</Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
        
        {/* Sidebar */}
        {callState === 'joined' && (
          <div className={`${showReportPanel ? 'w-96' : 'w-72'} bg-[#2A2A2A] p-4 flex flex-col gap-4 overflow-y-auto`}>
            <Button data-testid="visio-toggle-report" onClick={() => setShowReportPanel(!showReportPanel)} className="w-full bg-[#4A4A4A] hover:bg-[#5A5A5A]">
              <FileText className="h-4 w-4 mr-2" />
              {showReportPanel ? 'Masquer rapport' : 'Rédiger rapport'}
            </Button>
            
            {showReportPanel ? (
              <Card className="bg-[#3A3A3A] border-none">
                <CardHeader className="py-3">
                  <CardTitle className="text-white text-sm flex items-center justify-between">
                    Rapport {reportSaved && <CheckCircle className="h-4 w-4 text-green-500" />}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-white text-xs">Diagnostic</Label>
                    <Textarea value={reportData.diagnostic} onChange={(e) => setReportData({...reportData, diagnostic: e.target.value})} className="bg-[#4A4A4A] border-[#5A5A5A] text-white text-sm h-20" data-testid="visio-field-diagnostic" />
                  </div>
                  <div>
                    <Label className="text-white text-xs">Observations</Label>
                    <Textarea value={reportData.observations} onChange={(e) => setReportData({...reportData, observations: e.target.value})} className="bg-[#4A4A4A] border-[#5A5A5A] text-white text-sm h-20" data-testid="visio-field-observations" />
                  </div>
                  <div>
                    <Label className="text-white text-xs">Zone humide</Label>
                    <Textarea value={reportData.zone_humide} onChange={(e) => setReportData({...reportData, zone_humide: e.target.value})} className="bg-[#4A4A4A] border-[#5A5A5A] text-white text-sm h-16" placeholder="Localisation, étendue de l'humidité..." data-testid="visio-field-zone-humide" />
                  </div>
                  <div>
                    <Label className="text-white text-xs">Stigmates observés</Label>
                    <Textarea value={reportData.stigmates} onChange={(e) => setReportData({...reportData, stigmates: e.target.value})} className="bg-[#4A4A4A] border-[#5A5A5A] text-white text-sm h-16" placeholder="Fissures, auréoles, moisissures, gonflement..." data-testid="visio-field-stigmates" />
                  </div>
                  <div>
                    <Label className="text-white text-xs">Pièces sinistrées</Label>
                    <Textarea value={reportData.pieces_sinistrees} onChange={(e) => setReportData({...reportData, pieces_sinistrees: e.target.value})} className="bg-[#4A4A4A] border-[#5A5A5A] text-white text-sm h-16" placeholder="SDB, cuisine, chambre, plafond RDC..." data-testid="visio-field-pieces-sinistrees" />
                  </div>
                  <div>
                    <Label className="text-white text-xs">Recommandations</Label>
                    <Textarea value={reportData.recommandations} onChange={(e) => setReportData({...reportData, recommandations: e.target.value})} className="bg-[#4A4A4A] border-[#5A5A5A] text-white text-sm h-16" data-testid="visio-field-recommandations" />
                  </div>
                  <Button data-testid="visio-save-report" onClick={saveReport} className="w-full bg-green-600 hover:bg-green-700">
                    <Save className="h-4 w-4 mr-2" /> Sauvegarder
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[#3A3A3A] border-none">
                <CardHeader className="py-3">
                  <CardTitle className="text-white text-sm">Outils d'annotation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    {TOOLS.map((tool) => (
                      <Button
                        key={tool.id}
                        data-testid={`visio-tool-${tool.id}`}
                        size="sm"
                        onClick={() => setSelectedTool(tool.id)}
                        className={`flex flex-col items-center py-3 h-auto ${selectedTool === tool.id ? 'bg-[#8B7355]' : 'bg-[#4A4A4A] hover:bg-[#5A5A5A]'}`}
                      >
                        <tool.icon className="h-5 w-5" style={{ color: selectedTool === tool.id ? 'white' : tool.color }} />
                        <span className="text-[10px] mt-1 text-white">{tool.name}</span>
                      </Button>
                    ))}
                  </div>
                  <Button data-testid="visio-clear-annotations" onClick={clearAnnotations} className="w-full bg-[#4A4A4A] hover:bg-[#5A5A5A]">
                    <Trash2 className="h-4 w-4 mr-2" /> Effacer tout
                  </Button>
                </CardContent>
              </Card>
            )}
            
            <Button data-testid="visio-capture-photo" onClick={capturePhoto} className="w-full bg-blue-600 hover:bg-blue-700 h-12">
              <Camera className="h-5 w-5 mr-2" /> Capturer photo
            </Button>

            {session?.intervention_id && (
              <Button data-testid="visio-attach-dossier" onClick={attachToIntervention} disabled={attaching}
                className="w-full bg-[#8B7355] hover:bg-[#7A6548] h-12">
                {attaching ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : attached ? <CheckCircle className="h-5 w-5 mr-2" /> : <FolderPlus className="h-5 w-5 mr-2" />}
                {attached ? 'Enregistré' : 'Enregistrer dans le dossier'}
              </Button>
            )}

            {/* Documents à faire signer */}
            <Card className="bg-[#3A3A3A] border-none" data-testid="visio-docs-panel">
              <CardHeader className="py-3">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <FileSignature className="h-4 w-4 text-[#C9A26B]" /> Documents à faire signer
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {activeDoc && (
                  <div className="bg-amber-900/40 border border-amber-600/50 rounded-lg p-2 text-xs text-amber-200 flex items-center justify-between gap-2" data-testid="visio-active-doc">
                    <span className="flex items-center gap-1 truncate"><Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" /> En attente de signature : {activeDoc.name}</span>
                    <Button size="sm" onClick={unpushDoc} className="h-6 px-2 bg-[#4A4A4A] hover:bg-[#5A5A5A]" data-testid="visio-unpush-doc"><X className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
                {docs.length === 0 && !activeDoc && (
                  <p className="text-gray-400 text-xs text-center py-2">Aucun document. Ajoutez un PDF à faire signer.</p>
                )}
                {docs.map((d) => {
                  const isSigned = signedDocs.some((s) => (s.name || '').includes(d.name));
                  return (
                    <div key={d.id} className="bg-[#4A4A4A] rounded-lg p-2 flex items-center justify-between gap-2" data-testid={`visio-doc-${d.id}`}>
                      <span className="text-white text-xs truncate flex-1">{d.name}</span>
                      {isSigned ? (
                        <span className="text-green-400 text-[11px] flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5" /> Signé</span>
                      ) : (
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" onClick={() => pushDoc(d)} disabled={!!activeDoc}
                            className="h-7 px-2 bg-green-600 hover:bg-green-700 text-[11px]" data-testid={`visio-push-doc-${d.id}`}>
                            <Send className="h-3.5 w-3.5 mr-1" /> Envoyer
                          </Button>
                          <Button size="sm" onClick={() => setSignDoc(d)}
                            className="h-7 px-2 bg-[#8B7355] hover:bg-[#7A6548] text-[11px]" data-testid={`visio-sign-doc-${d.id}`}>
                            <FileSignature className="h-3.5 w-3.5 mr-1" /> Signer
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
                <input ref={docInputRef} type="file" accept="application/pdf" className="hidden" onChange={uploadSignDoc} data-testid="visio-doc-file-input" />
                <Button size="sm" onClick={() => docInputRef.current?.click()} disabled={uploadingDoc}
                  className="w-full bg-[#4A4A4A] hover:bg-[#5A5A5A] mt-1" data-testid="visio-add-doc-btn">
                  {uploadingDoc ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  Ajouter un PDF
                </Button>

                {signedDocs.length > 0 && (
                  <div className="pt-2 border-t border-[#5A5A5A] space-y-2">
                    <p className="text-[11px] text-green-400 font-medium">Documents signés ({signedDocs.length})</p>
                    {signedDocs.map((sd) => (
                      <div key={sd.id} className="bg-[#2E3A2E] rounded-lg p-2" data-testid={`visio-signed-doc-${sd.id}`}>
                        <p className="text-white text-xs truncate mb-1">{sd.name}</p>
                        {session?.intervention_id && <p className="text-green-400 text-[10px] mb-1">✓ Attaché au dossier</p>}
                        <div className="flex gap-1">
                          <a href={fileHref(sd.url)} target="_blank" rel="noreferrer" className="flex-1">
                            <Button size="sm" className="w-full h-7 bg-[#4A4A4A] hover:bg-[#5A5A5A] text-[10px]">Ouvrir</Button>
                          </a>
                          <Button size="sm" onClick={() => shareSigned(sd)} className="flex-1 h-7 bg-[#4A4A4A] hover:bg-[#5A5A5A] text-[10px]" data-testid={`visio-share-signed-${sd.id}`}>
                            <Copy className="h-3 w-3 mr-1" /> Copier
                          </Button>
                          <a href={`sms:${session?.client_phone || ''}?&body=${encodeURIComponent('Votre document signé KARDIF : ' + fileHref(sd.url))}`} className="flex-1">
                            <Button size="sm" className="w-full h-7 bg-[#4A4A4A] hover:bg-[#5A5A5A] text-[10px]"><MessageSquare className="h-3 w-3" /></Button>
                          </a>
                          <a href={`mailto:?subject=${encodeURIComponent('Votre document signé KARDIF')}&body=${encodeURIComponent('Votre document signé : ' + fileHref(sd.url))}`} className="flex-1">
                            <Button size="sm" className="w-full h-7 bg-[#4A4A4A] hover:bg-[#5A5A5A] text-[10px]"><Mail className="h-3 w-3" /></Button>
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card className="bg-[#3A3A3A] border-none flex-1">
              <CardHeader className="py-3">
                <CardTitle className="text-white text-sm flex justify-between">
                  Photos ({capturedPhotos.length})
                  {capturedPhotos.length > 0 && (
                    <Button data-testid="visio-download-zip" size="sm" variant="ghost" onClick={downloadPhotosAsZip} className="h-6 px-2 text-white">
                      <Archive className="h-4 w-4" />
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-48 overflow-y-auto">
                {capturedPhotos.map((p, i) => (
                  <img key={i} src={p.data} alt="" className="w-full h-16 object-cover rounded" />
                ))}
                {capturedPhotos.length === 0 && <p className="text-gray-400 text-sm text-center py-4">Aucune photo</p>}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      {/* Controls */}
      {callState === 'joined' && (
        <div className="bg-[#2A2A2A] px-4 py-3 flex items-center justify-center gap-4">
          <Button data-testid="visio-tech-mute" onClick={toggleMute} className={`rounded-full h-12 w-12 ${isMuted ? 'bg-red-500' : 'bg-gray-600'}`}>
            {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </Button>
          <Button data-testid="visio-tech-video" onClick={toggleVideo} className={`rounded-full h-12 w-12 ${isVideoOff ? 'bg-red-500' : 'bg-gray-600'}`}>
            {isVideoOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
          </Button>
          <Button data-testid="visio-end-call" onClick={endCall} className="rounded-full h-14 w-14 bg-red-600 hover:bg-red-700">
            <PhoneOff className="h-6 w-6" />
          </Button>
        </div>
      )}

      {/* Dialog : le technicien signe/place lui-même */}
      <Dialog open={!!signDoc} onOpenChange={(o) => { if (!o) setSignDoc(null); }}>
        <DialogContent className="max-w-md bg-white max-h-[90vh] overflow-y-auto" data-testid="visio-tech-sign-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#2D2D2D] text-base">Signer : {signDoc?.name}</DialogTitle>
          </DialogHeader>
          {signDoc && (
            <SignatureFlow
              pdfUrl={fileHref(signDoc.url)}
              docName={signDoc.name}
              busy={signingTech}
              onComplete={techSignComplete}
              onCancel={() => setSignDoc(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
